import { z } from 'zod'
import { ActorNameSchema, FileRefSchema } from '@/shared/lib'

// Документ. Живёт в слоте; владелец (entity_kind/entity_id) выводится из слота.
// Read-модель со срезом смотрящего: draft/visible/uploaded-флаги и tags у
// заказчика отсутствуют (опциональны), невидимые ему документы бэк не отдаёт.

// Владельцы документарной части — те же, что у слотов.
export const DOCUMENT_OWNER_KINDS = ['project', 'company', 'task', 'invoice', 'meeting'] as const
export const DocumentOwnerKindSchema = z.enum(DOCUMENT_OWNER_KINDS)
export type DocumentOwnerKind = z.infer<typeof DocumentOwnerKindSchema>

export const DocumentVersionSchema = z.object({
  number: z.number(),
  file: FileRefSchema,
  uploaded_by: ActorNameSchema,
  uploaded_at: z.string(),
  note: z.string().optional(),
})
export type DocumentVersion = z.infer<typeof DocumentVersionSchema>

export const DocumentApprovalSchema = z.object({
  version_number: z.number(),
  by: ActorNameSchema,
  at: z.string(),
})
export type DocumentApproval = z.infer<typeof DocumentApprovalSchema>

export const DocumentReadSchema = z.object({
  id: z.string(),
  entity_kind: DocumentOwnerKindSchema,
  entity_id: z.string(),
  slot_id: z.string(),
  title: z.string(),
  order_in_slot: z.number(),
  current_version: DocumentVersionSchema,
  approval: DocumentApprovalSchema.optional(),
  version: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  draft: z.boolean().optional(),
  visible_to_customer: z.boolean().optional(),
  uploaded_by_customer: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
})
export type DocumentRead = z.infer<typeof DocumentReadSchema>

// Ссылка на проект-владельца у проектных строк листинга scope=with-projects.
export const DocumentProjectRefSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
})
export type DocumentProjectRef = z.infer<typeof DocumentProjectRefSchema>

// Строка плоского листинга документов сущности (brief: без файла — только
// номер и дата версии). Группировку по slot_id делает потребитель. project —
// только у сделочных строк листинга компании scope=with-projects.
export const DocumentSummarySchema = z.object({
  id: z.string(),
  slot_id: z.string(),
  // Слот-владелец документа (id + имя) — для группировки листинга по слотам.
  slot: z.object({ id: z.string(), name: z.string() }),
  title: z.string(),
  order_in_slot: z.number(),
  current_version: z.object({ number: z.number(), uploaded_at: z.string() }),
  approval: z.object({ version_number: z.number(), at: z.string() }).optional(),
  draft: z.boolean().optional(),
  visible_to_customer: z.boolean().optional(),
  uploaded_by_customer: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  project: DocumentProjectRefSchema.optional(),
})
export type DocumentSummary = z.infer<typeof DocumentSummarySchema>

export const DocumentViewEntrySchema = z.object({
  viewer: ActorNameSchema,
  viewed_at: z.string(),
})
export type DocumentViewEntry = z.infer<typeof DocumentViewEntrySchema>
